import React, { useState, useRef, useEffect } from "react";
import { Send, Loader2, Mic, Square, Paperclip, X, FileText, Image as ImageIcon, FileAudio, FileVideo, File as FileIcon } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { sttApi } from "../lib/api";
import { FilePicker } from "./FilePicker";

/**
 * Silent text chat box. Sends the pasted / typed content to the text-only
 * LLM endpoint (Gemini 3.5 Flash via Emergent LLM key). Glitch replies in
 * the transcript – NO voice audio.
 *
 * The mic button records audio via MediaRecorder, uploads to /api/transcribe
 * (OpenAI Whisper), and drops the transcript into the textarea for the user
 * to edit before sending.
 */

// Pick a MIME type MediaRecorder can produce that Whisper accepts.
const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];
  for (const t of candidates) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
};

const extFromMime = (m) => {
  if (m.includes("mp4")) return "m4a";
  if (m.includes("ogg")) return "ogg";
  return "webm";
};

export const TextInputBox = ({ onSend }) => {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [attachment, setAttachment] = useState(null); // {id, filename, content_type, size}
  const ref = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const mimeRef = useRef("");

  const submit = async () => {
    const trimmed = text.trim();
    if ((!trimmed && !attachment) || sending) return;
    setSending(true);
    try {
      const payload = attachment ? { text: trimmed, attachment } : trimmed;
      const ok = await onSend(payload);
      if (ok !== false) {
        setText("");
        setAttachment(null);
      }
    } catch (_e) {
      toast.error("Message failed to send");
    } finally {
      setSending(false);
      ref.current?.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const startRecording = async () => {
    if (recording || transcribing || sending) return;
    if (typeof MediaRecorder === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const denied = e?.name === "NotAllowedError" || e?.name === "SecurityError";
      toast.error(
        denied
          ? "Microphone permission denied — enable it in browser settings"
          : "Couldn't access microphone"
      );
      return;
    }
    const mime = pickMimeType();
    mimeRef.current = mime;
    let recorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (e) {
      stream.getTracks().forEach((t) => t.stop());
      toast.error("Couldn't start recorder");
      return;
    }
    chunksRef.current = [];
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      const mimeType = mimeRef.current || recorder.mimeType || "audio/webm";
      const blob = new Blob(chunksRef.current, { type: mimeType });
      chunksRef.current = [];
      if (blob.size < 500) {
        toast.error("Recording too short");
        return;
      }
      setTranscribing(true);
      try {
        const filename = `recording.${extFromMime(mimeType)}`;
        const res = await sttApi.transcribe(blob, filename);
        const transcript = (res?.text || "").trim();
        if (!transcript) {
          toast.error("No speech detected");
          return;
        }
        // Append to current text so users can dictate + type together
        setText((prev) =>
          prev.trim() ? `${prev.trim()} ${transcript}` : transcript
        );
        setTimeout(() => ref.current?.focus(), 0);
      } catch (e) {
        toast.error(
          e?.response?.data?.detail || e?.message || "Transcription failed"
        );
      } finally {
        setTranscribing(false);
      }
    };
    recorderRef.current = recorder;
    streamRef.current = stream;
    recorder.start();
    setRecording(true);
    setElapsed(0);
    const startedAt = Date.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startedAt) / 1000);
      setElapsed(secs);
      // Hard-stop at 10 minutes to stay under Whisper's 25 MB cap
      if (secs >= 600) stopRecording();
    }, 250);
  };

  const stopRecording = () => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch (_e) {
        /* ignore */
      }
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
    setElapsed(0);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const s = streamRef.current;
      if (s) s.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const mm = Math.floor(elapsed / 60);
  const ss = String(elapsed % 60).padStart(2, "0");

  const iconFor = (ctype = "", name = "") => {
    const c = (ctype || "").toLowerCase();
    if (c.startsWith("image/")) return ImageIcon;
    if (c.startsWith("audio/")) return FileAudio;
    if (c.startsWith("video/")) return FileVideo;
    if (c === "application/pdf" || c.startsWith("text/")) return FileText;
    return FileIcon;
  };
  const AttIcon = attachment ? iconFor(attachment.content_type, attachment.filename) : null;

  return (
    <div
      data-testid="text-input-box"
      className="w-full max-w-2xl mx-auto px-4 shrink-0"
    >
      {attachment && (
        <div
          data-testid="text-input-attachment-chip"
          className="flex items-center gap-2 mb-1.5 rounded-lg border border-blue-400/25 bg-blue-500/10 px-3 py-1.5 text-xs text-blue-100"
        >
          <AttIcon className="h-4 w-4 text-blue-300 shrink-0" />
          <span className="truncate flex-1">{attachment.filename}</span>
          <button
            type="button"
            data-testid="text-input-attachment-clear"
            onClick={() => setAttachment(null)}
            className="text-blue-200/70 hover:text-white transition-colors"
            aria-label="Remove attachment"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
      <div className="glass rounded-2xl border border-white/10 pl-3 pr-2 py-2.5 flex items-end gap-2">
        <Textarea
          ref={ref}
          data-testid="text-input-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          placeholder={
            recording
              ? `Listening… ${mm}:${ss}  ·  tap the square to stop`
              : transcribing
              ? "Transcribing…"
              : attachment
              ? `Ask Glitch about ${attachment.filename}…`
              : "Type, paste, or tap the mic to dictate. Enter to send, Shift+Enter for newline."
          }
          className="min-h-[52px] max-h-[160px] resize-none bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm py-2 px-1 text-white/90 placeholder:text-white/35 leading-snug"
          disabled={sending || recording || transcribing}
        />

        {/* Attach button */}
        <Button
          data-testid="text-input-attach"
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={sending || recording || transcribing}
          size="icon"
          className="h-9 w-9 rounded-full shrink-0 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 disabled:opacity-40 transition-colors"
          aria-label="Attach a file"
          title="Attach a file"
        >
          <Paperclip className="h-4 w-4" />
        </Button>

        {/* Mic / stop button */}
        <Button
          data-testid={recording ? "text-input-stop-mic" : "text-input-mic"}
          type="button"
          onClick={recording ? stopRecording : startRecording}
          disabled={sending || transcribing}
          size="icon"
          className={`h-9 w-9 rounded-full shrink-0 disabled:opacity-40 transition-colors ${
            recording
              ? "bg-red-500/90 hover:bg-red-500 text-white ring-2 ring-red-400/40 animate-pulse"
              : "bg-white/5 hover:bg-white/10 text-white/80 border border-white/10"
          }`}
          aria-label={recording ? "Stop recording" : "Dictate"}
          title={recording ? "Stop recording" : "Dictate (records via your mic)"}
        >
          {transcribing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : recording ? (
            <Square className="h-3.5 w-3.5" fill="currentColor" />
          ) : (
            <Mic className="h-4 w-4" />
          )}
        </Button>

        {/* Send button */}
        <Button
          data-testid="text-input-send"
          type="button"
          onClick={submit}
          disabled={(!text.trim() && !attachment) || sending || recording || transcribing}
          size="icon"
          className="h-9 w-9 rounded-full bg-blue-600 hover:bg-blue-500 shrink-0 disabled:opacity-40"
          aria-label="Send"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>

      <FilePicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={(f) =>
          setAttachment({
            id: f.id,
            filename: f.filename,
            content_type: f.content_type,
            size: f.size,
            has_text: f.has_text,
          })
        }
      />
    </div>
  );
};
